"use client";

import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import styles from "./ReservedTimes.module.css";
import defaultImage from "../../assets/images/freeface.jpg";
import { ReactComponent as CartIcon } from "../../assets/icons/CartIcon.svg";
import { ReactComponent as ArrowIcon } from "../../assets/icons/arrow.svg";
import { ReactComponent as ClockIcon } from "../../assets/icons/ClockIcon.svg";
import { ReactComponent as StatusCirclePending } from "../../assets/icons/card-status-pending-icon-ligh.svg";
import { ReactComponent as StatusCircle } from "../../assets/icons/card-status-paied-icon-ligh.svg";

import { getCustomerTimes } from "../../services/server-actions";
import { MobileCustomerProfileTimeDto } from "../../services/shared/service-proxies/service-proxies";
import { DateTime } from "ts-luxon";
import moment from "moment-jalaali";

moment.loadPersian({ dialect: "persian-modern", usePersianDigits: true });

const ReservedTimes: React.FC = () => {
  const [reservations, setReservations] = useState<MobileCustomerProfileTimeDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const categories = useMemo(() => {
    return JSON.parse(localStorage.getItem("categories") || "[]");
  }, []);

  const partners = useMemo(() => {
    return JSON.parse(localStorage.getItem("partners") || "[]");
  }, []);

  useEffect(() => {
    async function fetchReservations() {
      try {
        setLoading(true);
        const times = await getCustomerTimes();
        setReservations(times);
      } catch (error) {
        console.error("❌ خطا در دریافت تایم‌های کاربر:", error);
        setReservations([]);
      } finally {
        setLoading(false);
      }
    }

    fetchReservations();
  }, []);

  if (loading) return <p></p>;
  if (reservations.length === 0) return null;

  return (
    <div>
      <h6 className={styles.title}>وقت‌های رزرو شده شما</h6>
      <div className={styles.horizontalScroll}>
        {reservations.map((reservation) => {
          const service = categories.find((c: any) => c.id === reservation.categoryId);
          const serviceTitle = service ? service.name : "خدمات عمومی";

          const stylist = partners.find((p: any) => p.id === reservation.partnerId);
          const stylistName = stylist
            ? `${stylist.firstname} ${stylist.lastname}`
            : "آرایشگر ناشناس";

          const stylistImage = stylist?.profile || defaultImage;

          const displayedDate = reservation.onDate
            ? moment(reservation.onDate.toString()).format("jYYYY/jMM/jDD")
            : "نامشخص";

          const isPaid = (reservation as any).status === "paied";

          return (
            <div
            key={`${reservation.partnerId}-${reservation.categoryId}-${reservation.fromTime}`}

              className={isPaid ? styles.card : styles.cardPending}
            >
              <div className={isPaid ? styles.cardHeader : styles.cardPendingHeader}>
                <div className={styles.statusSection}>
                  {isPaid ? (
                    <>
                      <StatusCircle />
                      <span className={styles.statusText}>رزرو شده</span>
                    </>
                  ) : (
                    <>
                      <StatusCirclePending />
                      <span className={styles.statusText}>در انتظار پرداخت</span>
                    </>
                  )}
                </div>

                <Link
                  to="/service-details"
                  state={{
                    service: serviceTitle,
                    stylist: stylistName,
                    profileImage: stylistImage,
                    partnerId: reservation.partnerId,
                    phone: stylist?.phone || "نامشخص",
                    reservation: reservation,
                    fromTime: reservation.fromTime,
                    toTime: reservation.toTime,
                    onDate: reservation.onDate,
                    selectedPersianDate: displayedDate,
                    categoryId: reservation.categoryId, // برای لینک برگشت
                    from: "main",
                  }}
                  className={styles.detailsText}
                >
                  <span className={styles.detailsLink}>جزئیات</span>
                  <ArrowIcon className={styles.arrowIcon} />
                </Link>
              </div>

              <div className={isPaid ? styles.cardBody : styles.cardBodyPending}>
                <img
                  src={stylistImage}
                  alt={stylistName}
                  className={styles.stylistImage}
                />
                <div className={styles.textContainer}>
                  <h6 className={styles.serviceTitle}>{serviceTitle}</h6>
                  <p className={styles.stylistName}>{stylistName}</p>
                </div>
              </div>

              <div className={isPaid ? styles.cardFooter : styles.cardFooterPending}>
                <div className={styles.timeContainer}>
                  <CartIcon className={`${styles.timeIcon} ${styles.timeSvg}`} />
                  <span className={styles.timeText}>{displayedDate}</span>
                </div>

                {reservation.fromTime && reservation.toTime && (
                  <div className={styles.timeContainer}>
                    <span className={styles.timeIcon}>
                      <ClockIcon className={styles.clockImage} />
                    </span>
                    <span className={styles.timeText}>
                      {DateTime.fromISO(reservation.fromTime).toFormat("HH:mm")} تا{" "}
                      {DateTime.fromISO(reservation.toTime).toFormat("HH:mm")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReservedTimes;
